import { apiUrl } from '@/lib/shell';

/** A refused runtime control call, with the status the server answered. */
export class ControlRefused extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null = null,
    // A refused App Check says which kind of finding (never a value).
    readonly reason: string | null = null,
  ) {
    super('runtime_control_refused');
  }
}

export type RuntimeControlAction = 'bootstrap' | 'renew' | 'revoke';

const controlPath = (artifactId: string, action: RuntimeControlAction) =>
  `/api/artifacts/${encodeURIComponent(artifactId)}/runtime/${action}`;

/** One runtime control call for the exact saved selection, on the session it belongs to. */
export async function control(
  session: typeof fetch,
  artifactId: string,
  action: RuntimeControlAction,
  body: Record<string, string>,
) {
  const response = await session(apiUrl(controlPath(artifactId, action)), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ControlRefused(
      response.status,
      typeof body?.error === 'string' ? body.error : null,
      typeof body?.reason === 'string' ? body.reason : null,
    );
  }
  return action === 'revoke' ? null : await response.json();
}

/** The exact saved selection a frame is opened for, with the account and company it belongs to. */
export type BootstrapSelection = {
  companyId: string;
  actorId: string;
  artifactId: string;
  revisionId: string;
  versionId: string;
  purpose: string;
};

const keyOf = (s: BootstrapSelection) =>
  [s.companyId, s.actorId, s.artifactId, s.revisionId, s.versionId, s.purpose].join('\0');

// A page that opens a saved App asks for its runtime ticket at the same time as the App's saved view,
// instead of after it. The frame for exactly that selection takes it once; the server still checks the
// current document when it issues the ticket and again when the frame redeems it. The captured fetch
// identity binds a ticket to its original session, even across A→B→A. A ticket lives 60 s
// and is used once, so an unused one simply expires.
const started = new Map<string, { at: number; session: typeof fetch; issued: Promise<unknown> }>();
const FRESH_MS = 30_000;

/** Start the runtime bootstrap for a saved App a page is about to open. */
export function prefetchBootstrap(session: typeof fetch, selection: BootstrapSelection) {
  const key = keyOf(selection);
  const now = Date.now();
  for (const [other, entry] of started) if (now - entry.at >= FRESH_MS) started.delete(other);
  if (started.get(key)?.session === session) return;
  const issued = control(session, selection.artifactId, 'bootstrap', {
    revisionId: selection.revisionId,
    versionId: selection.versionId,
    purpose: selection.purpose,
  });
  // Nothing is reported here: the frame that takes it handles the outcome as its own call.
  issued.catch(() => {});
  started.set(key, { at: now, session, issued });
}

/** A known App opening may mount its frame while its saved display is still loading.
 * This is only a scheduling hint; the server still admits the exact selection. */
export function hasBootstrap(session: typeof fetch, selection: BootstrapSelection): boolean {
  const entry = started.get(keyOf(selection));
  return Boolean(entry?.session === session && Date.now() - entry.at < FRESH_MS);
}

/** The prefetched bootstrap for exactly this selection, once; null when there is none (or it is stale). */
export function takeBootstrap(
  session: typeof fetch,
  selection: BootstrapSelection,
): Promise<unknown> | null {
  const key = keyOf(selection);
  const entry = started.get(key);
  if (entry?.session !== session) return null;
  started.delete(key);
  return entry && Date.now() - entry.at < FRESH_MS ? entry.issued : null;
}
